import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RIGHTS } from '@constants/user';
import usePermissions from '@hooks/usePermissions';

export const useHasAccessToAsset = () => {
    const {
        isOwner,
        hasFullAccess,
        hasFullAccessAt,
        hasFullAccessAtBrand,
        hasFullSettings,
        hasLocationAccess,
        hasBrandAccess,
    } = usePermissions();
    const companies = useSelector(state => state.account?.account?.companies) ?? [];

    // Inherited brand access: a location-scoped user can read brand-scoped assets
    // if any of their accessible locations belongs to that brand.
    const inheritedBrandIds = useMemo(() => {
        const brandIds = new Set();
        for (const company of companies) {
            if (company.brand?.id) {
                brandIds.add(company.brand.id);
            }
        }
        return brandIds;
    }, [companies]);

    const hasBrandReadAccess = useCallback(
        brandId => hasBrandAccess(brandId) || inheritedBrandIds.has(brandId),
        [hasBrandAccess, inheritedBrandIds],
    );

    // Single-location/brand asset: WRITE requires module:full AND scope coverage on the SAME team
    const hasAccessToAssetById = useCallback(
        (asset, module) => {
            if (isOwner) {
                return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
            }

            if (asset.locationId) {
                const inScope = hasLocationAccess(asset.locationId);
                return { [RIGHTS.READ]: inScope, [RIGHTS.WRITE]: hasFullAccessAt(module, asset.locationId) };
            }

            if (asset.brandId) {
                return {
                    [RIGHTS.READ]: hasBrandReadAccess(asset.brandId),
                    [RIGHTS.WRITE]: hasFullAccessAtBrand(module, asset.brandId),
                };
            }

            // No location or brand on asset — module:full is enough
            return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: hasFullAccess(module) };
        },
        [isOwner, hasFullAccess, hasFullAccessAt, hasFullAccessAtBrand, hasLocationAccess, hasBrandReadAccess],
    );

    const hasAccessToChannel = useCallback(() => isOwner || hasFullSettings(), [isOwner, hasFullSettings]);

    // Multi-location/brand asset: WRITE requires module:full AND scope coverage on the SAME team
    const hasAccessToAssetByIds = useCallback(
        (asset, module) => {
            if (isOwner) {
                return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: true };
            }

            const locationIds = asset.locations || [];
            const brandIds = asset.brands || [];

            if (locationIds.length === 0 && brandIds.length === 0) {
                return { [RIGHTS.READ]: true, [RIGHTS.WRITE]: hasFullAccess(module) };
            }

            const locationsWritable = locationIds.length === 0 || locationIds.every(id => hasFullAccessAt(module, id));
            const brandsWritable = brandIds.length === 0 || brandIds.every(id => hasFullAccessAtBrand(module, id));
            const anyReadable =
                locationIds.some(id => hasLocationAccess(id)) ||
                brandIds.some(id => hasBrandReadAccess(id)) ||
                (locationIds.length === 0 && brandIds.length === 0);

            return {
                [RIGHTS.READ]: anyReadable,
                [RIGHTS.WRITE]: locationsWritable && brandsWritable,
            };
        },
        [isOwner, hasFullAccess, hasFullAccessAt, hasFullAccessAtBrand, hasLocationAccess, hasBrandReadAccess],
    );

    return {
        hasAccessToChannel,
        hasAccessToAssetByIds,
        hasAccessToAssetById,
    };
};
